/**
 * Advanced image-text carousel component
 * - Only activates when scrolling DOWN into the component (not when scrolling UP)
 * - Single-step display - one scroll shows complete item (image+text)
 * - Carousel can only be completed by viewing all items and exiting from the last item
 * - Users can reenter if they exit early (from first item)
 * - Precise item transitions with optimized scroll handling
 */
document.addEventListener('DOMContentLoaded', function() {
  // Desktop implementation (for viewport width > 768px)
  if (window.innerWidth > 768) {
    // Configurable parameters
    const SCROLL_THRESHOLD = 400; // Scroll threshold for item switching
    const RE_ENTRY_DISTANCE = 500; // Distance required to scroll before re-entry is allowed
    
    // Get all containers
    const containers = document.querySelectorAll('.joe-image-text_container');
    
    containers.forEach(container => {
      const wrapper = container.querySelector('.joe-image-text_wrapper');
      const items = Array.from(container.querySelectorAll('.joe-image-text_item'));
      
      if (!items.length) return;
      
      // Set initial styles
      items.forEach(item => {
        item.style.position = 'absolute';
        item.style.top = '0';
        item.style.left = '0';
        item.style.width = '100%';
        item.style.height = '100%';
        item.style.opacity = '0';
        
        const textEl = item.querySelector('.joe-image-text_item-text');
        if (textEl) {
          textEl.style.opacity = '0';
        }
      });
      
      // State management
      const state = {
        isFixed: false,
        busy: false,
        index: 0,
        fixedAt: 0,
        justEntered: false,
        lastExitPosition: null,
        canReEnter: true,
        scrollAmount: 0,
        carouselCompleted: false // Track if user has seen all slides and exited from last slide
      };
      
      // Create placeholder element
      const spacer = document.createElement('div');
      spacer.style.width = '100%';
      spacer.style.display = 'none';
      container.insertBefore(spacer, wrapper);
      
      // Global scroll direction detection
      let lastY = window.scrollY;
      let scrollingDown = true;
      
      window.addEventListener('scroll', () => {
        scrollingDown = window.scrollY > lastY;
        lastY = window.scrollY;
        
        // Check if scrolled enough distance to allow re-entry
        if (!state.canReEnter && state.lastExitPosition !== null && !state.carouselCompleted) {
          const scrollDistance = Math.abs(window.scrollY - state.lastExitPosition);
          if (scrollDistance >= RE_ENTRY_DISTANCE) {
            state.canReEnter = true;
            state.lastExitPosition = null;
          }
        }
      });
      
      // Center alignment check function
      function checkCenterAlignment() {
        // Only allow activation if:
        // 1. Component can be re-entered
        // 2. User is scrolling DOWN into it
        // 3. Carousel has not been completed yet
        if (!state.canReEnter || state.isFixed || state.busy || !scrollingDown || state.carouselCompleted) return false;
        
        const rect = wrapper.getBoundingClientRect();
        const viewHeight = window.innerHeight;
        
        // Calculate center points
        const elementCenter = rect.top + rect.height / 2;
        const viewportCenter = viewHeight / 2;
        
        // Tolerance value
        const tolerance = 50;
        
        // Check if center-aligned
        if (Math.abs(elementCenter - viewportCenter) < tolerance) {
          state.index = 0;
          state.justEntered = true;
          return true;
        }
        return false;
      }

      // IntersectionObserver setup
      const observer = new IntersectionObserver(entries => {
        entries.forEach(entry => {
          if (entry.isIntersecting && !state.isFixed && !state.busy) {
            if (checkCenterAlignment()) {
              fixElement();
            }
          }
        });
      }, {
        threshold: [0.1, 0.2, 0.3, 0.4, 0.5, 0.6, 0.7, 0.8, 0.9],
        rootMargin: '0px'
      });

      window.addEventListener('scroll', function() {
        if (!state.isFixed && !state.busy) {
          if (checkCenterAlignment()) {
            fixElement();
          }
        }
      }, { passive: true });
      
      observer.observe(wrapper);
      
      // Fix element function
      function fixElement() {
        if (state.isFixed || state.busy) return;
        state.busy = true;
        
        // Record position
        state.fixedAt = window.scrollY;
        
        // Prepare placeholder
        spacer.style.height = wrapper.offsetHeight + 'px';
        spacer.style.display = 'block';
        
        // Fix container - modify style to keep scrollbar visible
        wrapper.style.position = 'fixed';
        wrapper.style.top = '0';
        wrapper.style.left = '0';
        wrapper.style.width = '100%';
        wrapper.style.height = '100vh';
        wrapper.style.zIndex = '5';
        
        // Reset scroll accumulation
        state.scrollAmount = 0;
        
        // Update display
        updateDisplay();
        
        // Add wheel event - ensure all scroll is intercepted
        window.addEventListener('wheel', handleWheel, {passive: false});
        
        // Add scroll capture event to intercept all scrolling
        window.addEventListener('scroll', preventScroll, {passive: false, capture: true});
        document.addEventListener('touchmove', preventScroll, {passive: false, capture: true});
        
        state.isFixed = true;
        
        // Don't hide scrollbar, but prevent page scrolling from affecting view
        document.documentElement.style.overflowY = 'scroll'; // Force scrollbar display
        document.documentElement.style.position = 'relative'; // Prevent page scrolling
        
        setTimeout(() => {
          state.busy = false;
          // Give user enough time to adjust to fixed state before allowing exit
          setTimeout(() => {
            state.justEntered = false;
          }, 600);
        }, 300);
      }
      
      // Prevent default scroll behavior
      function preventScroll(e) {
        if (state.isFixed) {
          e.preventDefault();
          e.stopPropagation();
          return false;
        }
      }
      
      // Unfix element function with carousel completion tracking
      function unfixElement(exitDirection) {
        if (!state.isFixed || state.busy) return;
        state.busy = true;
        
        // Remove event listeners
        window.removeEventListener('wheel', handleWheel);
        window.removeEventListener('scroll', preventScroll, {capture: true});
        document.removeEventListener('touchmove', preventScroll, {capture: true});
        
        // Record exit position
        state.lastExitPosition = window.scrollY;
        
        // Set carousel completion status:
        // If exited from the last item (bottom exit), mark as completed
        // Otherwise, keep it as incomplete (can be re-entered)
        if (exitDirection === 'bottom' && state.index === items.length - 1) {
          state.carouselCompleted = true; // Mark carousel as complete - cannot be re-entered
        }
        
        // Always prevent immediate re-entry
        state.canReEnter = false;
        
        // Save original height and position info
        const originalHeight = wrapper.offsetHeight;
        const fixedAtPosition = state.fixedAt;
        
        // Add transition effect
        wrapper.style.transition = "transform 0.3s ease-out";
        
        // Apply slight translation based on exit direction
        if (exitDirection === 'top') {
          wrapper.style.transform = `translateY(${originalHeight * 0.01}px)`;
        } else if (exitDirection === 'bottom') {
          wrapper.style.transform = `translateY(${-originalHeight * 0.01}px)`;
        }
        
        // Restore original document scroll behavior
        document.documentElement.style.overflow = '';
        document.documentElement.style.position = '';
        
        let targetPosition;
        if (exitDirection === 'top') {
          targetPosition = Math.max(0, fixedAtPosition - originalHeight * 0.3);
          setTimeout(() => {
            resetAndScroll();
          }, 300);
        } else if (exitDirection === 'bottom') {
          setTimeout(() => {
            wrapper.style.position = '';
            wrapper.style.top = '';
            wrapper.style.left = '';
            wrapper.style.width = '';
            wrapper.style.height = '';
            wrapper.style.zIndex = '';
            
            items.forEach(item => {
              item.classList.remove('item_show');
              const textEl = item.querySelector('.joe-image-text_item-text');
              if (textEl) textEl.classList.remove('item_show');
            });
            
            targetPosition = fixedAtPosition + originalHeight * 0.4;
            
            window.scrollTo({
              top: targetPosition,
              behavior: 'auto'
            });
            
            setTimeout(() => {
              wrapper.style.transition = "";
              wrapper.style.transform = "";
              spacer.style.display = 'none';
              state.isFixed = false;
              state.busy = false;
            }, 0);
          }, 200);
        }
        
        function resetAndScroll() {
          wrapper.style.transition = "";
          wrapper.style.transform = "";
          wrapper.style.position = '';
          wrapper.style.top = '';
          wrapper.style.left = '';
          wrapper.style.width = '';
          wrapper.style.height = '';
          wrapper.style.zIndex = '';
          
          items.forEach(item => {
            item.classList.remove('item_show');
            const textEl = item.querySelector('.joe-image-text_item-text');
            if (textEl) textEl.classList.remove('item_show');
          });
          
          spacer.style.display = 'none';
          
          window.scrollTo({
            top: targetPosition,
            behavior: 'auto'
          });
          
          state.isFixed = false;
          setTimeout(() => { state.busy = false; }, 300);
        }
      }
      
      // Update display - show image and text together
      function updateDisplay() {
        // Hide all items
        items.forEach(item => {
          item.style.opacity = '0';
          item.classList.remove('item_show');
          const textEl = item.querySelector('.joe-image-text_item-text');
          if (textEl) {
            textEl.style.opacity = '0';
            textEl.classList.remove('item_show');
          }
        });
        
        // Show current item - set as active state (image+text)
        const currentItem = items[state.index];
        if (currentItem) {
          currentItem.style.opacity = '1';
          currentItem.classList.add('item_show');
          
          const textEl = currentItem.querySelector('.joe-image-text_item-text');
          if (textEl) textEl.style.opacity = '1';
        }
      }
      
      // Wheel handling function - switch between items
      function handleWheel(e) {
        if (!state.isFixed || state.busy) return;
        
        // Prevent default scrolling
        e.preventDefault();
        e.stopPropagation();
        
        state.scrollAmount += e.deltaY;
        
        if (Math.abs(state.scrollAmount) >= SCROLL_THRESHOLD) {
          const scrollDown = state.scrollAmount > 0;
          
          if (scrollDown) {
            // Scroll down, show next item
            if (state.index < items.length - 1) {
              state.index++;
            } else if (!state.justEntered) {
              // If already at last item, exit component
              unfixElement('bottom');
              state.scrollAmount = 0;
              return;
            }
          } else {
            // Scroll up, show previous item
            if (state.index > 0) {
              state.index--;
            } else if (!state.justEntered) {
              // If already at first item, exit component
              unfixElement('top');
              state.scrollAmount = 0;
              return;
            }
          }
          
          updateDisplay();
          state.scrollAmount = 0;
        }
        
        return false;
      }
    });
  } else {
    // Mobile implementation
    // Get all containers
    const containers = document.querySelectorAll('.joe-image-text_container');
    
    containers.forEach(container => {
      const wrapper = container.querySelector('.joe-image-text_wrapper');
      const items = Array.from(container.querySelectorAll('.joe-image-text_item'));
      
      if (!items.length) return;
      
      // Create placeholder element
      const spacer = document.createElement('div');
      spacer.style.width = '100%';
      spacer.style.display = 'none';
      container.insertBefore(spacer, wrapper);
      
      // State object
      const state = {
        isFixed: false,
        busy: false,
        index: 0,
        lastTouchY: 0,
        touchStartY: 0,
        scrollAmount: 0,
        fixedAt: 0,
        justEntered: true,
        lastExitPosition: null,
        canReEnter: true,
        carouselCompleted: false, // Track if user has seen all slides and exited from last slide
        viewportHeight: window.innerHeight // 新增：存储当前视口高度
      };
      
      // Mobile scroll threshold
      const MOBILE_SCROLL_THRESHOLD = 150;
      const RE_ENTRY_DISTANCE = 350;
      
      // 新增：视口大小变化处理函数
      function handleViewportChange() {
        // 更新视口高度
        state.viewportHeight = window.innerHeight;
        
        // 如果当前是固定状态，更新元素高度以覆盖整个屏幕
        if (state.isFixed) {
          updateFixedElementDimensions();
        }
      }
      
      // 新增：更新固定元素尺寸以覆盖整个屏幕
      function updateFixedElementDimensions() {
        wrapper.style.height = `${state.viewportHeight}px`;
        wrapper.style.width = '100%';
        
        // 确保内部元素也适应新的视口大小
        const activeItem = items[state.index];
        if (activeItem) {
          activeItem.style.height = `${state.viewportHeight}px`;
          activeItem.style.width = '100%';
        }
      }
      
      // 新增：添加视口变化监听器
      window.addEventListener('resize', handleViewportChange, { passive: true });
      window.addEventListener('orientationchange', handleViewportChange, { passive: true });
      
      // Global scroll direction detection
      let lastY = window.scrollY;
      let scrollingDown = true;
      
      window.addEventListener('scroll', () => {
        scrollingDown = window.scrollY > lastY;
        lastY = window.scrollY;
        
        // Check if scrolled enough distance to allow re-entry
        if (!state.canReEnter && state.lastExitPosition !== null && !state.carouselCompleted) {
          const scrollDistance = Math.abs(window.scrollY - state.lastExitPosition);
          if (scrollDistance >= RE_ENTRY_DISTANCE) {
            state.canReEnter = true;
            state.lastExitPosition = null;
          }
        }
      }, { passive: true });
      
      // Center alignment check
      function checkCenterAlignment() {
        // Only allow activation if:
        // 1. Component can be re-entered
        // 2. User is scrolling DOWN into it
        // 3. Carousel has not been completed yet
        if (!state.canReEnter || state.isFixed || state.busy || !scrollingDown || state.carouselCompleted) return false;
        
        const rect = wrapper.getBoundingClientRect();
        const viewHeight = window.innerHeight;
        
        const elementCenter = rect.top + rect.height / 2;
        const viewportCenter = viewHeight / 2;
        
        // Tolerance value
        const tolerance = 80;
        
        if (Math.abs(elementCenter - viewportCenter) < tolerance) {
          // Initialize with first item
          state.index = 0;
          state.justEntered = true;
          return true;
        }
        return false;
      }
      
      // Monitor component viewport visibility
      const observer = new IntersectionObserver(entries => {
        entries.forEach(entry => {
          if (entry.isIntersecting && !state.isFixed && !state.busy) {
            if (checkCenterAlignment()) {
              fixElement();
            }
          }
        });
      }, {
        threshold: [0.3, 0.4, 0.5, 0.6, 0.7],
        rootMargin: '0px'
      });
      
      observer.observe(wrapper);
      
      // Scroll event to check center alignment
      window.addEventListener('scroll', function() {
        if (!state.isFixed && !state.busy) {
          if (checkCenterAlignment()) {
            fixElement();
          }
        }
      }, { passive: true });
      
      // Fix element - 已修改
      function fixElement() {
        if (state.isFixed || state.busy) return;
        state.busy = true;
        
        // 更新视口高度
        state.viewportHeight = window.innerHeight;
        
        // Record position
        state.fixedAt = window.scrollY;
        
        // Prepare placeholder
        spacer.style.height = wrapper.offsetHeight + 'px';
        spacer.style.display = 'block';
        
        // Fix container
        wrapper.style.position = 'fixed';
        wrapper.style.top = '0';
        wrapper.style.left = '0';
        wrapper.style.width = '100%';
        wrapper.style.height = `${state.viewportHeight}px`; // 使用实际视口高度
        wrapper.style.zIndex = '5';
        
        // Add touch event handling
        wrapper.addEventListener('touchstart', handleTouchStart, { passive: false });
        wrapper.addEventListener('touchmove', handleTouchMove, { passive: false });
        wrapper.addEventListener('touchend', handleTouchEnd, { passive: false });
        
        // Initialize scroll amount
        state.scrollAmount = 0;
        
        // Update display
        updateDisplay();
        
        state.isFixed = true;
        
        // Prevent page scrolling
        document.body.style.overflow = 'hidden';
        
        // 确保内部元素也覆盖整个视口
        const currentItem = items[state.index];
        if (currentItem) {
          currentItem.style.height = `${state.viewportHeight}px`;
          currentItem.style.width = '100%';
        }
        
        setTimeout(() => {
          state.busy = false;
          // Delay allowing exit
          setTimeout(() => {
            state.justEntered = false;
          }, 600);
        }, 300);
      }
      
      // Unfix element - 已修改
      function unfixElement(exitDirection) {
        if (!state.isFixed || state.busy) return;
        state.busy = true;
        
        // Remove touch event listeners
        wrapper.removeEventListener('touchstart', handleTouchStart);
        wrapper.removeEventListener('touchmove', handleTouchMove);
        wrapper.removeEventListener('touchend', handleTouchEnd);
        
        // Record exit position
        state.lastExitPosition = window.scrollY;
        
        // Set carousel completion status
        if (exitDirection === 'bottom' && state.index === items.length - 1) {
          state.carouselCompleted = true; // Mark carousel as complete - cannot be re-entered
        }
        
        // Always prevent immediate re-entry
        state.canReEnter = false;
        
        // Add transition effect
        wrapper.style.transition = "transform 0.3s ease-out";
        
        // Calculate target position
        let targetPosition;
        
        if (exitDirection === 'top') {
          wrapper.style.transform = 'translateY(-50px)';
          targetPosition = Math.max(0, state.fixedAt - window.innerHeight * 0.2);
        } else {
          wrapper.style.transform = 'translateY(50px)';
          targetPosition = state.fixedAt + window.innerHeight * 0.2;
        }
        
        // Reset styles and scroll
        setTimeout(() => {
          wrapper.style.transition = "";
          wrapper.style.transform = "";
          wrapper.style.position = '';
          wrapper.style.top = '';
          wrapper.style.left = '';
          wrapper.style.width = '';
          wrapper.style.height = '';
          wrapper.style.zIndex = '';
          
          // 重置所有项目高度
          items.forEach(item => {
            item.classList.remove('item_show');
            item.style.height = '';
            item.style.width = '';
          });
          
          spacer.style.display = 'none';
          
          // Restore page scrolling
          document.body.style.overflow = '';
          
          // Scroll to target position
          window.scrollTo({
            top: targetPosition,
            behavior: 'auto'
          });
          
          state.isFixed = false;
          setTimeout(() => { state.busy = false; }, 300);
        }, 300);
      }
      
      // Update display
      function updateDisplay() {
        // Hide all items
        items.forEach(item => {
          item.style.opacity = '0';
          item.classList.remove('item_show');
        });
        
        // Show current item as active state
        const currentItem = items[state.index];
        if (currentItem) {
          currentItem.style.opacity = '1';
          currentItem.classList.add('item_show');
        }
      }
      
      // Touch start event
      function handleTouchStart(e) {
        if (!state.isFixed) return;
        
        e.preventDefault();
        state.touchStartY = e.touches[0].clientY;
        state.lastTouchY = state.touchStartY;
      }
      
      // Touch move event
      function handleTouchMove(e) {
        if (!state.isFixed) return;
        
        e.preventDefault();
        const currentY = e.touches[0].clientY;
        const deltaY = state.lastTouchY - currentY; // Up is positive, down is negative
        
        // Accumulate scroll amount
        state.scrollAmount += deltaY;
        state.lastTouchY = currentY;
        
        // Check if threshold reached
        if (Math.abs(state.scrollAmount) >= MOBILE_SCROLL_THRESHOLD) {
          // Determine swipe direction: up is positive, down is negative
          const swipeUp = state.scrollAmount > 0;
          
          // Handle swipe
          handleSwipe(swipeUp);
          
          // Reset accumulation
          state.scrollAmount = 0;
        }
      }
      
      // Touch end event
      function handleTouchEnd(e) {
        if (!state.isFixed) return;
        
        e.preventDefault();
        
        // If accumulated amount exceeds half threshold, trigger swipe
        if (Math.abs(state.scrollAmount) >= MOBILE_SCROLL_THRESHOLD / 2) {
          const swipeUp = state.scrollAmount > 0;
          handleSwipe(swipeUp);
        }
        
        // Reset accumulation
        state.scrollAmount = 0;
      }
      
      // Handle swipe
      function handleSwipe(swipeUp) {
        if (state.busy) return;
        
        // Swipe up - show next item
        if (swipeUp) {
          if (state.index < items.length - 1) {
            state.index++;
          } else if (!state.justEntered) {
            // If last item, exit component
            unfixElement('bottom');
            return;
          }
        } 
        // Swipe down - show previous item
        else {
          if (state.index > 0) {
            state.index--;
          } else if (!state.justEntered) {
            // If first item, exit component
            unfixElement('top');
            return;
          }
        }
        
        // Update display
        updateDisplay();
      }
      
      // 新增：添加页面可见性变化监听
      document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'visible' && state.isFixed) {
          // 当页面从隐藏变为可见时，重新检查并更新视口尺寸
          handleViewportChange();
        }
      });

      // 新增：监听虚拟键盘事件
      window.addEventListener('focusin', () => {
        if (state.isFixed) {
          // 给键盘弹出时间进行调整
          setTimeout(handleViewportChange, 300);
        }
      });

      window.addEventListener('focusout', () => {
        if (state.isFixed) {
          // 键盘收起后调整
          setTimeout(handleViewportChange, 300);
        }
      });
    });
  }
});